import { useMemo } from "react";
import { useGetMany, type Identifier } from "ra-core";
import { Link } from "react-router";
import type { Contact } from "@/components/atomic-crm/types";
import { getContactShowPath } from "@/app/routing";

const contactLabel = (contact: Contact) =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() ||
  `Contact #${contact.id}`;

/** Current names for the contacts referenced by an account-level list. */
export const useContactNames = (ids: Array<Identifier | null | undefined>) => {
  const unique = useMemo(
    () => [
      ...new Set(ids.filter((id): id is Identifier => id != null).map(String)),
    ],
    [ids],
  );
  const { data = [] } = useGetMany<Contact>(
    "contacts",
    { ids: unique },
    { enabled: unique.length > 0, staleTime: 30_000 },
  );
  return useMemo(
    () =>
      new Map(
        data.map((contact) => [String(contact.id), contactLabel(contact)]),
      ),
    [data],
  );
};

/** Which person an account-level record belongs to. */
export const ClientContactTag = ({
  contactId,
  names,
}: {
  contactId?: Identifier | null;
  names: Map<string, string>;
}) => {
  if (contactId == null) {
    return <span className="text-muted-foreground">Account</span>;
  }
  return (
    <Link
      to={getContactShowPath(contactId)}
      className="link-action truncate"
      onClick={(event) => event.stopPropagation()}
    >
      {names.get(String(contactId)) ?? `Contact #${contactId}`}
    </Link>
  );
};
